export default {
  id: 'discipline-score',
  title: 'Discipline Score',
  content: `## Discipline Score

Your Discipline Score is a number from **0 to 100** that reflects the quality of your trading decisions — not your profit or loss.

You can have a high discipline score with a losing week. You can have a low discipline score with a winning week. Over time, a high discipline score is the best predictor of consistent results.

> The Discipline Score measures your process. The market measures your outcome.

---

## Where to Find It

- **Home dashboard** — shown prominently as a widget, tap to open the full report
- **Profile → Discipline Report** — detailed breakdown with history

---

## What the Score Measures

| Factor | What is checked |
|---|---|
| Plan completeness | Did you always define entry, stop loss, target, and quantity before entering? |
| R:R discipline | What % of your trades had a R:R of at least 2:1? |
| Stop loss adherence | Did you exit when your stop was hit, or did you hold through it? |
| Overtrading | Are you taking more trades per day than is consistent with your style? |
| Revenge trading | Are you placing low-quality trades immediately after a loss? |
| Position sizing | Are your positions within your stated Risk Per Trade % limit? |
| ACT consistency | Are your ACT Enter/Skip decisions consistent with the setups? |

---

## Score Ranges

| Score | What it means |
|---|---|
| 80–100 | Excellent discipline — decisions are consistent, planned, and risk-aware |
| 60–80 | Good — with identifiable areas to improve |
| 40–60 | Moderate — patterns of impulsive or undisciplined decisions detected |
| Below 40 | Significant behavioural issues present — review your analytics |

---

## How it is Calculated

The score is a weighted composite across your recent trading decisions (last 30 days). ACT sessions contribute alongside live paper trades.

Weights emphasise:
- **Risk management** — stop loss discipline and position sizing (highest weight)
- **Plan quality** — whether you had a plan before entering
- **Decision quality** — ACT scores and live trade outcomes vs plan

The score is recomputed weekly (Sunday evening IST) by the discipline analysis job. Day-to-day changes reflect your most recent decisions entering and old ones ageing out.

---

## Improving Your Score

- Always set a stop loss and target before placing a trade
- Never skip the trade plan in ACT — incomplete plans score zero
- Exit when your stop is hit — do not move it to avoid a loss
- Do not increase position size after a loss to recover faster
- Aim for R:R ≥ 2.0 on every trade

---

## The Readiness Benchmark

A Discipline Score of **70 or above consistently** over 30+ days is TradeLab's benchmark before a user should consider deploying real capital. This is not a guarantee of success — it is an indication that your habits are sound.`,
};
